import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getApiUrl } from "../config/api";

type SubscriptionData = {
  subscription?: any | null;
  hasActive: boolean;
  maxStores?: number | null;
} | null;

type BillingContextValue = {
  data: SubscriptionData;
  refresh: () => Promise<void>;
  setHasActive: (value: boolean) => void;
};

const BillingContext = createContext<BillingContextValue | undefined>(
  undefined
);

let fetchedOnce = false;

export const BillingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [data, setData] = useState<SubscriptionData>(null);
  const fetchingRef = useRef(false);

  const refresh = useMemo(
    () => async () => {
      if (fetchingRef.current) return;
      fetchingRef.current = true;
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(getApiUrl("/api/billing/me"), {
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });
        if (!res.ok) throw new Error("failed");
        const json = await res.json();
        // Не затираем предыдущее состояние, если backend вернул временную ошибку
        if (json?.error === "temporary_unavailable") {
          // оставляем прошлое значение, но попробуем обновить позже
          return;
        }
        setData({
          subscription: json?.subscription ?? null,
          hasActive: !!json?.hasActive,
          maxStores:
            typeof json?.maxStores === "number"
              ? json.maxStores
              : (json?.subscription?.plan?.maxStores ?? null),
        });
      } catch (_) {
        // В случае сетевой ошибки оставляем предыдущее состояние
        // и не принудительно снимаем hasActive, чтобы избежать ложных редиректов
      } finally {
        fetchingRef.current = false;
      }
    },
    []
  );

  useEffect(() => {
    if (!fetchedOnce) {
      fetchedOnce = true;
      refresh();
    }
  }, [refresh]);

  const setHasActive = (value: boolean) => {
    setData((prev) => ({
      ...(prev || { subscription: null, maxStores: null, hasActive: false }),
      hasActive: value,
    }));
  };

  const value = useMemo(
    () => ({ data, refresh, setHasActive }),
    [data, refresh]
  );

  return (
    <BillingContext.Provider value={value}>{children}</BillingContext.Provider>
  );
};

export const useBilling = () => {
  const ctx = useContext(BillingContext);
  if (!ctx) throw new Error("useBilling must be used within BillingProvider");
  return ctx;
};
